import React, { useContext } from 'react';
import Styled from 'styled';
import { FormattedMessage } from 'react-intl';
import { RouteComponentProps } from 'react-router';
import { useQuery, useMutation } from '@apollo/client/react/hooks';

import { Field, Form, Formik, FormikProps, FieldProps } from 'formik';

import Checkbox from 'four-nets/ui-v2/form/checkbox/checkbox';
import { AlertContext } from 'lib/alert-context/alert-context';
import RadioButton from 'four-nets/ui-v2/form/radio-input/radio-input';

import defaultStyles from './settings-notifications.scss';
import { NotificationMailingEnum as NotificationMailing } from './notifications-enum';
import query, { SettingsNotificationQuery, SettingsNotificationQueryVariables } from './settings-notifications.graphql';
import mutation, {
    SettingsNotificationUpdateMutation,
    SettingsNotificationUpdateMutationVariables,
} from './mutation.graphql';

interface Values {
    newsletterOn: boolean;
    internalPostOn: boolean;
    bazaarNotificationsOn: boolean;
    notificationMailingOn: boolean;
    notificationMailingLevel: any;
}

export interface Props extends RouteComponentProps<{ username: string }> {
    styles: typeof defaultStyles;
}

function transformPartOfTextToBold(msg: string) {
    const words = msg.split(' ');
    const preBold = words.slice(0, 3).join(' ') + ' ';
    const boldText = words[3];
    const postBold = ' ' + words.slice(4).join(' ');

    return (
        <React.Fragment>
            <span>{preBold}</span>
            <span>
                <b>{boldText}</b>
            </span>
            <span>{postBold}</span>
        </React.Fragment>
    );
}

const SettingsNotifications: React.FC<Props> = (props, context) => {
    const {
        styles,
        match: {
            params: { username },
        },
    } = props;

    const { fail } = useContext(AlertContext);

    const { data } = useQuery<SettingsNotificationQuery, SettingsNotificationQueryVariables>(query, {
        variables: { username },
    });
    const [updateNotificationSettings] =
        useMutation<SettingsNotificationUpdateMutation, SettingsNotificationUpdateMutationVariables>(mutation);

    if (
        !data ||
        !data.users ||
        !data.users.edges ||
        data.users.edges.length !== 1 ||
        !data.users.edges[0] ||
        !data.users.edges[0].node ||
        !data.users.edges[0].node.notificationSettings
    ) {
        return null;
    }

    const user = data.users.edges[0].node;
    const notificationSettings = user.notificationSettings!;

    return (
        <React.Fragment>
            <Formik
                onSubmit={(values, { resetForm }) => {
                    updateNotificationSettings({
                        variables: {
                            input: {
                                id: user.id,
                                bazaarNotificationsOn: values.bazaarNotificationsOn,
                                newsletterOn: values.newsletterOn,
                                internalPostOn: values.internalPostOn,
                                notificationMailing: values.notificationMailingLevel,
                            },
                        },
                    })
                        .then(() => {
                            resetForm();
                        })
                        .catch((reason: any) => {
                            fail!({
                                body: JSON.stringify(reason),
                                onAccept: () => window.location.reload(true),
                            });
                        });
                }}
                initialValues={{
                    internalPostOn: notificationSettings.internalPostOn || false,
                    bazaarNotificationsOn: notificationSettings.bazaarNotificationsOn || false,
                    notificationMailingOn: notificationSettings.notificationMailing != NotificationMailing.OFF,
                    notificationMailingLevel: notificationSettings.notificationMailing || NotificationMailing.OFF,
                    newsletterOn: notificationSettings.newsletterOn || false,
                }}
            >
                {({ dirty, values, setFieldValue }: FormikProps<Values>) => (
                    <React.Fragment>
                        <FormattedMessage id='SETTINGS_NOTIFICATION_TITLE' values={{ email: user.email }}>
                            {msg => <h4 className={styles.partOneHeader}>{transformPartOfTextToBold(msg + ':')}</h4>}
                        </FormattedMessage>
                        <Form className={styles.form}>
                            <Field name='internalPostOn'>
                                {({ field }: FieldProps) => (
                                    <Checkbox
                                        style={{ wrapper: styles.checkboxWrapper, label: styles.checkboxLabel }}
                                        {...field}
                                        id={field.name}
                                        label={<FormattedMessage id='INTERNAL_MESSAGE' />}
                                    />
                                )}
                            </Field>
                            <Field name='bazaarNotificationsOn'>
                                {({ field }: FieldProps) => (
                                    <Checkbox
                                        style={{ wrapper: styles.checkboxWrapper, label: styles.checkboxLabel }}
                                        {...field}
                                        id={field.name}
                                        label={<FormattedMessage id='MESSAGES_FROM_BAZAAR' />}
                                    />
                                )}
                            </Field>
                            <Field name='notificationMailingOn'>
                                {({ field }: FieldProps) => (
                                    <Checkbox
                                        style={{ wrapper: styles.checkboxWrapper, label: styles.checkboxLabel }}
                                        {...field}
                                        id={field.name}
                                        onChange={event => {
                                            field.onChange(event);
                                            if (event.target.checked) {
                                                setFieldValue('notificationMailingLevel', NotificationMailing.MODERATE);
                                            } else {
                                                setFieldValue('notificationMailingLevel', NotificationMailing.OFF);
                                            }
                                        }}
                                        label={<FormattedMessage id='NOTIFICATIONS_WITH_EXAMPLES' />}
                                    />
                                )}
                            </Field>
                            <Field name='notificationMailingLevel' disabled={!values.notificationMailingOn}>
                                {({ field }: FieldProps) => (
                                    <div className={styles.notificationsVerbosityWrapper}>
                                        <RadioButton
                                            style={{ label: styles.radioButtonLabel }}
                                            id={String(NotificationMailing.IMMEDIATE)}
                                            field={field}
                                            disabled={!values.notificationMailingOn}
                                            label={<FormattedMessage id='NOTIFY_OFTEN' />}
                                        />
                                        <RadioButton
                                            style={{ label: styles.radioButtonLabel }}
                                            id={String(NotificationMailing.MODERATE)}
                                            field={field}
                                            disabled={!values.notificationMailingOn}
                                            label={<FormattedMessage id='NOTIFY_MODERATE' />}
                                        />
                                    </div>
                                )}
                            </Field>
                            <FormattedMessage id='TURN_ON_NEWSLETTER_WITH_REASON'>
                                {msg => (
                                    <h4 className={styles.partTwoHeader}>{transformPartOfTextToBold(msg + ':')}</h4>
                                )}
                            </FormattedMessage>
                            <Field name='newsletterOn'>
                                {({ field }: FieldProps) => (
                                    <Checkbox
                                        style={{ wrapper: styles.checkboxWrapper, label: styles.checkboxLabel }}
                                        {...field}
                                        id={field.name}
                                        label={<FormattedMessage id='NEWSLETTER' />}
                                    />
                                )}
                            </Field>
                            <div className={styles.submitButtonRow}>
                                <button disabled={!dirty} className={styles.submitButton}>
                                    <FormattedMessage id='SAVE_CHANGES' values={{ gender: 'v' }} />
                                </button>
                            </div>
                        </Form>
                    </React.Fragment>
                )}
            </Formik>
        </React.Fragment>
    );
};

export default Styled(defaultStyles)(SettingsNotifications);
